import React, { useState, useEffect, useRef } from 'react';

const ROUNDS = ['3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', 'Js', 'Qs', 'Ks'];

export default function FiveCrownsGame({
  activeGame,
  gameState,
  onUpdateGameState,
  onEndGame,
  onQuitGame,
  onEditGame,
  socket,
  isHistoricalView = false
}) {
  const allPlayers = activeGame.players || [];

  // Player order - can be reordered independently of scores
  // Default to the order from activeGame.players (by turn_order)
  const [playerOrder, setPlayerOrder] = useState(() => {
    if (gameState.playerOrder) return gameState.playerOrder;
    return allPlayers.map(p => p.user_id);
  });

  // Get ordered players based on playerOrder
  const players = playerOrder.map(uid => allPlayers.find(p => p.user_id === uid)).filter(Boolean);

  // Find dealer by user_id, not index
  const dealerUserId = gameState.dealerUserId || allPlayers.find(p => p.is_dealer)?.user_id || allPlayers[0]?.user_id;
  const dealerIndex = players.findIndex(p => p.user_id === dealerUserId);

  // Initialize scores from game state - now keyed by user_id
  // scores = { [user_id]: [round scores array] }
  const [scores, setScores] = useState(() => {
    if (gameState.scores && typeof gameState.scores === 'object' && !Array.isArray(gameState.scores)) {
      // New format: scores keyed by user_id
      return gameState.scores;
    }
    // Migrate from old format or initialize empty
    if (gameState.scores && Array.isArray(gameState.scores)) {
      // Old format: convert from array to object
      const migratedScores = {};
      allPlayers.forEach((player, i) => {
        migratedScores[player.user_id] = gameState.scores[i] || Array(11).fill('');
      });
      return migratedScores;
    }
    // Initialize empty scores for all players
    const emptyScores = {};
    allPlayers.forEach(player => {
      emptyScores[player.user_id] = Array(11).fill('');
    });
    return emptyScores;
  });

  const [shareStatus, setShareStatus] = useState('');
  const [exportStatus, setExportStatus] = useState('');
  const [gameLog, setGameLog] = useState(() => {
    // Initialize game log from persisted state
    return gameState.gameLog || [];
  });
  const [showGameLog, setShowGameLog] = useState(true);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const dragOverColumn = useRef(null);
  const [showRoundSummary, setShowRoundSummary] = useState(false);
  const [completedRound, setCompletedRound] = useState(null);
  const prevRoundRef = useRef(null);

  // Debounce refs for score logging
  const scoreDebounceTimers = useRef({});
  const pendingScoreUpdates = useRef({});

  // Touch drag state
  const touchDragState = useRef({
    isDragging: false,
    startY: 0,
    draggedUserId: null,
    longPressTimer: null
  });

  // Helper to merge game logs without losing entries
  // This prevents accidental overwrites when stale data is saved
  const mergeGameLogs = (existingLog, incomingLog) => {
    if (!incomingLog || incomingLog.length === 0) return existingLog;
    if (!existingLog || existingLog.length === 0) return incomingLog;

    // Create a map of existing entries by ID
    const existingIds = new Set(existingLog.map(entry => entry.id));

    // Find entries in incoming that don't exist in current
    const newEntries = incomingLog.filter(entry => !existingIds.has(entry.id));

    // Merge all entries and sort by ID (timestamp) descending
    const merged = [...existingLog, ...newEntries];
    merged.sort((a, b) => b.id - a.id);

    return merged;
  };

  // Sync scores, playerOrder, and gameLog with game state
  useEffect(() => {
    if (gameState.scores && typeof gameState.scores === 'object' && !Array.isArray(gameState.scores)) {
      setScores(gameState.scores);
    }
    if (gameState.playerOrder) {
      setPlayerOrder(gameState.playerOrder);
    }
    if (gameState.gameLog) {
      // Merge incoming log with existing to prevent accidental overwrites
      setGameLog(currentLog => mergeGameLogs(currentLog, gameState.gameLog));
    }
  }, [gameState.scores, gameState.playerOrder, gameState.gameLog]);

  // Listen for real-time updates
  useEffect(() => {
    if (socket) {
      // Listen for single cell updates (now using user_id)
      socket.on('score-cell-update', ({ userId, roundIndex, value }) => {
        setScores(prev => {
          const newScores = { ...prev };
          if (!newScores[userId]) newScores[userId] = Array(11).fill('');
          newScores[userId] = [...newScores[userId]];
          newScores[userId][roundIndex] = value;
          return newScores;
        });
      });

      // Listen for player order changes
      socket.on('player-order-update', ({ playerOrder: newOrder }) => {
        setPlayerOrder(newOrder);
      });

      // Listen for full game state updates
      socket.on('game-state', (state) => {
        if (state.scores && typeof state.scores === 'object' && !Array.isArray(state.scores)) {
          setScores(state.scores);
        }
        if (state.playerOrder) {
          setPlayerOrder(state.playerOrder);
        }
      });
    }
  }, [socket]);

  const updateScore = (userId, roundIndex, value) => {
    const player = allPlayers.find(p => p.user_id === userId);
    const playerName = player?.name || 'Unknown';
    const oldValue = scores[userId]?.[roundIndex];
    const key = `${userId}-${roundIndex}`;

    // Store initial value for debounced logging (only on first change)
    if (!pendingScoreUpdates.current[key]) {
      pendingScoreUpdates.current[key] = {
        initialValue: oldValue,
        userId,
        roundIndex,
        playerName
      };
    }

    const newScores = { ...scores };
    if (!newScores[userId]) newScores[userId] = Array(11).fill('');
    newScores[userId] = [...newScores[userId]];
    newScores[userId][roundIndex] = value;
    setScores(newScores);

    // Broadcast single cell update to other players immediately (uses user_id, not index)
    if (socket) {
      socket.emit('score-cell-update', {
        gameCode: activeGame.id,
        userId,
        roundIndex,
        value
      });
    }

    // Clear existing debounce timer for this cell
    if (scoreDebounceTimers.current[key]) {
      clearTimeout(scoreDebounceTimers.current[key]);
    }

    // Debounce the logging and backend save (800ms delay)
    scoreDebounceTimers.current[key] = setTimeout(() => {
      const pending = pendingScoreUpdates.current[key];
      if (!pending) return;

      const initialValue = pending.initialValue;
      const finalValue = value;
      const roundName = ROUNDS[roundIndex];
      const timestamp = new Date().toLocaleTimeString();

      let newLogEntry = null;
      if (initialValue === '' || initialValue === undefined) {
        if (finalValue !== '') {
          newLogEntry = {
            id: Date.now(),
            time: timestamp,
            message: `${playerName} scored ${finalValue} points for round ${roundName}`,
            type: 'score'
          };
        }
      } else if (finalValue !== initialValue) {
        newLogEntry = {
          id: Date.now(),
          time: timestamp,
          message: `${playerName}'s score for round ${roundName} was changed from ${initialValue} to ${finalValue || '(cleared)'}`,
          type: 'edit'
        };
      }

      // Clear pending update
      delete pendingScoreUpdates.current[key];
      delete scoreDebounceTimers.current[key];

      // Get latest state for saving - use functional updates to avoid stale closures
      setScores(currentScores => {
        setGameLog(currentGameLog => {
          const updatedGameLog = newLogEntry ? [newLogEntry, ...currentGameLog] : currentGameLog;
          // Save to backend - includes gameLog
          onUpdateGameState({ ...gameState, scores: currentScores, playerOrder, dealerUserId, gameLog: updatedGameLog });
          return updatedGameLog;
        });
        return currentScores;
      });
    }, 800);
  };

  const getTotal = (userId) => {
    return scores[userId]?.reduce((sum, score) => sum + (parseInt(score) || 0), 0) || 0;
  };

  const hasAnyScores = () => {
    return players.some(p => scores[p.user_id]?.some(s => s !== ''));
  };

  const isGameComplete = () => {
    return players.every(p =>
      ROUNDS.every((_, r) => scores[p.user_id]?.[r] !== '' && scores[p.user_id]?.[r] !== undefined)
    );
  };

  const getLeaderboard = () => {
    const playerData = players.map((player, i) => ({
      ...player,
      total: getTotal(player.user_id),
      index: i
    }));

    if (!hasAnyScores()) {
      return playerData;
    }

    return playerData.sort((a, b) => a.total - b.total);
  };

  const getCurrentRound = () => {
    for (let r = 0; r < ROUNDS.length; r++) {
      const allFilled = players.every(p => scores[p.user_id]?.[r] !== '' && scores[p.user_id]?.[r] !== undefined);
      if (!allFilled) return r;
    }
    return ROUNDS.length;
  };

  // Detect when a round completes and show the summary popup (with delay for multi-digit entry)
  const roundSummaryTimerRef = useRef(null);
  const lastTriggeredRoundRef = useRef(null);

  useEffect(() => {
    const currentRound = getCurrentRound();

    // Only trigger when round advances (not on initial load) and game isn't complete
    // Also check that we haven't already triggered for this round
    if (prevRoundRef.current !== null &&
        currentRound > prevRoundRef.current &&
        currentRound !== lastTriggeredRoundRef.current &&
        currentRound < ROUNDS.length &&
        !isHistoricalView) {

      // Mark this round as triggered so we don't re-trigger
      const completedRoundNum = prevRoundRef.current;
      lastTriggeredRoundRef.current = currentRound;

      // Clear any pending timer and start a new one
      if (roundSummaryTimerRef.current) {
        clearTimeout(roundSummaryTimerRef.current);
      }

      roundSummaryTimerRef.current = setTimeout(() => {
        setCompletedRound(completedRoundNum);
        setShowRoundSummary(true);
      }, 1500);
    }

    prevRoundRef.current = currentRound;
  }, [scores]);

  // Cleanup timer on unmount only
  useEffect(() => {
    return () => {
      if (roundSummaryTimerRef.current) {
        clearTimeout(roundSummaryTimerRef.current);
      }
    };
  }, []);

  const getMedal = (rank) => {
    if (rank === 0) return '🥇';
    if (rank === 1) return '🥈';
    if (rank === 2) return '🥉';
    return `#${rank + 1}`;
  };

  const getRank = (userId, leaderboard) => {
    if (!hasAnyScores()) return null;
    const playerTotal = getTotal(userId);
    let rank = 0;
    let prevTotal = null;
    for (let i = 0; i < leaderboard.length; i++) {
      if (leaderboard[i].total !== prevTotal) {
        rank = i;
        prevTotal = leaderboard[i].total;
      }
      if (leaderboard[i].user_id === userId) {
        return rank;
      }
    }
    return 0;
  };

  const handleFinishGame = () => {
    const finalScores = players.map(player => ({
      user_id: player.user_id,
      score: getTotal(player.user_id),
      round_scores: (scores[player.user_id] || []).map(s => parseInt(s) || 0)
    }));

    // Sort by score (lowest first for Five Crowns)
    finalScores.sort((a, b) => a.score - b.score);

    onEndGame(finalScores);
  };

  // Handle column drag for reordering players
  const handleColumnDragStart = (e, userId) => {
    setDraggedColumn(userId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColumnDragOver = (e, userId) => {
    e.preventDefault();
    dragOverColumn.current = userId;
  };

  const handleColumnDragEnd = () => {
    if (draggedColumn && dragOverColumn.current && draggedColumn !== dragOverColumn.current) {
      const newOrder = [...playerOrder];
      const fromIndex = newOrder.indexOf(draggedColumn);
      const toIndex = newOrder.indexOf(dragOverColumn.current);

      // Log the reorder
      const fromPlayer = allPlayers.find(p => p.user_id === draggedColumn);
      const timestamp = new Date().toLocaleTimeString();
      const newLogEntry = {
        id: Date.now(),
        time: timestamp,
        message: `${fromPlayer?.name} moved from position ${fromIndex + 1} to position ${toIndex + 1}`,
        type: 'reorder'
      };

      // Reorder the array
      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, draggedColumn);

      setPlayerOrder(newOrder);

      // Broadcast the order change
      if (socket) {
        socket.emit('player-order-update', {
          gameCode: activeGame.id,
          playerOrder: newOrder
        });
      }

      // Use functional update to get latest gameLog and avoid stale closure
      setGameLog(currentGameLog => {
        const newGameLog = [newLogEntry, ...currentGameLog];
        // Save to backend - includes gameLog
        onUpdateGameState({ ...gameState, scores, playerOrder: newOrder, dealerUserId, gameLog: newGameLog });
        return newGameLog;
      });
    }
    setDraggedColumn(null);
    dragOverColumn.current = null;
  };

  // Touch-based drag handlers to prevent scroll during drag
  const handleTouchStart = (e, userId) => {
    const touch = e.touches[0];
    touchDragState.current.startY = touch.clientY;
    touchDragState.current.draggedUserId = userId;

    // Long press to initiate drag (150ms)
    touchDragState.current.longPressTimer = setTimeout(() => {
      touchDragState.current.isDragging = true;
      setDraggedColumn(userId);
      // Haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(50);
    }, 150);
  };

  const handleTouchMove = (e, userId) => {
    // Cancel long press if moved too much before drag started
    if (!touchDragState.current.isDragging) {
      const touch = e.touches[0];
      const moveDistance = Math.abs(touch.clientY - touchDragState.current.startY);
      if (moveDistance > 10) {
        clearTimeout(touchDragState.current.longPressTimer);
        return;
      }
    }

    // Prevent scrolling while dragging
    if (touchDragState.current.isDragging) {
      e.preventDefault();
      dragOverColumn.current = userId;
    }
  };

  const handleTouchEnd = () => {
    clearTimeout(touchDragState.current.longPressTimer);

    if (touchDragState.current.isDragging) {
      handleColumnDragEnd();
    }

    touchDragState.current.isDragging = false;
    touchDragState.current.draggedUserId = null;
  };

  const shareScorecard = async () => {
    const leaderboard = getLeaderboard();
    const currentRound = getCurrentRound();
    const roundName = currentRound < ROUNDS.length ? ROUNDS[currentRound] : 'Complete';
    const currentDealer = (dealerIndex + currentRound) % players.length;
    const gameComplete = isGameComplete();
    const hasScores = hasAnyScores();

    let text = `👑 FIVE CROWNS 👑\n`;

    if (gameComplete) {
      const winner = leaderboard[0];
      text += `🏆 ${winner.name} WINS with ${winner.total} points! 🏆\n\n`;
    } else {
      text += `Round: ${roundName}\n`;
      text += `🃏 ${players[currentDealer]?.name} dealing\n\n`;
    }

    text += `🏆 STANDINGS:\n`;

    if (hasScores) {
      let prevTotal = null;
      let displayRank = 0;

      leaderboard.forEach((player, i) => {
        if (player.total !== prevTotal) {
          displayRank = i;
          prevTotal = player.total;
        }
        const medal = getMedal(displayRank);
        text += `${medal} ${player.name}: ${player.total}\n`;
      });
    } else {
      players.forEach((player, i) => {
        text += `${i + 1}. ${player.name}: –\n`;
      });
    }

    try {
      if (navigator.share) {
        await navigator.share({ text });
        setShareStatus('Shared!');
      } else {
        await navigator.clipboard.writeText(text);
        setShareStatus('Copied!');
      }
    } catch (err) {
      try {
        await navigator.clipboard.writeText(text);
        setShareStatus('Copied!');
      } catch (e) {
        setShareStatus('Failed');
      }
    }
    setTimeout(() => setShareStatus(''), 2000);
  };

  const exportGame = async () => {
    const gameData = {
      game: 'Five Crowns',
      date: new Date().toISOString(),
      join_code: activeGame.join_code,
      players: players.map(player => ({
        name: player.name,
        scores: scores[player.user_id] || [],
        total: getTotal(player.user_id)
      })),
      rounds: ROUNDS,
      currentRound: getCurrentRound(),
      isComplete: isGameComplete()
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(gameData, null, 2));
      setExportStatus('Copied!');
    } catch (err) {
      setExportStatus('Failed');
    }
    setTimeout(() => setExportStatus(''), 2000);
  };

  const copyAllGameData = async () => {
    const leaderboard = getLeaderboard();
    const gameData = {
      game: 'Five Crowns',
      exportedAt: new Date().toISOString(),
      gameId: activeGame.id,
      joinCode: activeGame.join_code,
      status: isGameComplete() ? 'complete' : 'in_progress',
      currentRound: getCurrentRound() + 1,
      roundName: ROUNDS[getCurrentRound()] || 'Complete',
      players: players.map((player, i) => ({
        name: player.name,
        id: player.user_id,
        displayOrder: i + 1,
        isDealer: player.user_id === dealerUserId,
        roundScores: ROUNDS.map((round, ri) => ({
          round,
          score: scores[player.user_id]?.[ri] !== '' ? parseInt(scores[player.user_id]?.[ri]) || 0 : null
        })),
        totalScore: getTotal(player.user_id),
        rank: getRank(player.user_id, leaderboard) + 1
      })),
      activityLog: gameLog.map(log => ({
        time: log.time,
        action: log.message,
        type: log.type
      })),
      leaderboard: leaderboard.map((player, rank) => ({
        rank: rank + 1,
        name: player.name,
        totalScore: player.total
      }))
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(gameData, null, 2));
      return true;
    } catch (err) {
      return false;
    }
  };

  const leaderboard = getLeaderboard();
  const currentRound = getCurrentRound();
  const currentDealer = (dealerIndex + currentRound) % players.length;
  const gameHasScores = hasAnyScores();
  const gameComplete = isGameComplete();

  return (
    <div className="p-2 bg-gradient-to-br from-emerald-900 to-emerald-950 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <button
          onClick={onQuitGame}
          className="text-emerald-300 hover:text-white text-sm"
        >
          ← {isHistoricalView ? 'Back' : 'Exit'}
        </button>
        <div className="text-center">
          <h1 className="text-xl font-bold text-amber-400">👑 Five Crowns 👑</h1>
          {isHistoricalView ? (
            <p className="text-emerald-300 text-xs">📜 Viewing Completed Game</p>
          ) : (
            <>
              <p className="text-emerald-300 text-xs">
                {gameComplete ? 'Game Complete!' : `Round: ${ROUNDS[currentRound]}`}
              </p>
              {!gameComplete && (
                <p className="text-amber-300 text-xs">
                  🃏 {players[currentDealer]?.name} dealing
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isHistoricalView && (
            <button
              onClick={onEditGame}
              className="text-emerald-300 hover:text-white text-sm"
              title="Edit game settings"
            >
              ⚙️
            </button>
          )}
          <div className="text-emerald-300 text-xs font-mono">
            {activeGame.join_code || ''}
          </div>
        </div>
      </div>

      {/* Winner Banner */}
      {gameComplete && (
        <div className="bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 rounded-xl p-4 mb-4 text-center">
          <div className="text-3xl mb-1">👑🎉👑</div>
          <div className="text-emerald-950 font-bold text-xl">{leaderboard[0]?.name} WINS!</div>
          <div className="text-emerald-900 text-sm">Final Score: {leaderboard[0]?.total} points</div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="bg-emerald-950/80 rounded-lg p-3 mb-2">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-amber-400 font-bold text-sm">🏆 LEADERBOARD</h2>
          <span className="text-emerald-400 text-xs bg-emerald-800 px-2 py-1 rounded">
            {gameComplete ? '✓ Complete' : `Round: ${ROUNDS[currentRound]}`}
          </span>
        </div>
        <div className="space-y-1">
          {leaderboard.map((player, i) => {
            const rank = getRank(player.user_id, leaderboard);
            const isLeader = rank === 0 && gameHasScores;
            const isWinner = gameComplete && rank === 0;

            return (
              <div
                key={player.user_id}
                className={`flex items-center justify-between px-3 py-1.5 rounded ${
                  isWinner ? 'bg-amber-500/40 border border-amber-400' :
                  isLeader ? 'bg-amber-500/30' :
                  rank === 1 ? 'bg-gray-400/20' :
                  rank === 2 ? 'bg-orange-600/20' :
                  'bg-emerald-800/30'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {!gameHasScores ? `${i + 1}.` : isWinner ? '👑' : getMedal(rank)}
                  </span>
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                  >
                    {player.name?.charAt(0)}
                  </div>
                  <span className={`font-medium ${isLeader ? 'text-amber-300' : 'text-white'}`}>
                    {player.name}
                  </span>
                </div>
                <span className={`font-bold font-mono text-lg ${isLeader ? 'text-amber-300' : 'text-white'}`}>
                  {gameHasScores ? player.total : '–'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-2 mb-2 flex-wrap">
        <button
          onClick={shareScorecard}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          📤 {shareStatus || 'Share'}
        </button>
        <button
          onClick={exportGame}
          className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          📋 {exportStatus || 'Export'}
        </button>
        {!isHistoricalView && (
          <button
            onClick={() => window.open(`/game/${activeGame.id}/tv`, '_blank')}
            className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            📺 TV Mode
          </button>
        )}
        {(gameComplete || isHistoricalView) && (
          <button
            onClick={handleFinishGame}
            className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 rounded-lg text-sm font-medium"
          >
            🏆 {isHistoricalView ? 'View Podium' : 'Finish Game'}
          </button>
        )}
      </div>

      {/* Score Table */}
      <div className="overflow-x-auto rounded-lg flex justify-center">
        <table className="text-sm">
          <thead>
            <tr className="bg-emerald-950">
              <th className="p-1.5 text-amber-400 text-xs sticky left-0 bg-emerald-950 z-10 min-w-10">Wild</th>
              {players.map((player, i) => {
                const rank = getRank(player.user_id, leaderboard);
                const isDealer = currentDealer === i && !gameComplete;
                const isLeader = rank === 0 && gameHasScores;
                const isWinner = gameComplete && rank === 0;
                const isDragging = draggedColumn === player.user_id;
                return (
                  <th
                    key={player.user_id}
                    draggable
                    onDragStart={(e) => handleColumnDragStart(e, player.user_id)}
                    onDragOver={(e) => handleColumnDragOver(e, player.user_id)}
                    onDragEnd={handleColumnDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, player.user_id)}
                    onTouchMove={(e) => handleTouchMove(e, player.user_id)}
                    onTouchEnd={handleTouchEnd}
                    className={`p-1 min-w-20 transition-all cursor-grab active:cursor-grabbing select-none ${
                      isDealer ? 'bg-amber-500/20' : ''
                    } ${isWinner ? 'bg-amber-500/30' : ''} ${isDragging ? 'opacity-50' : ''}`}
                    title="Drag to reorder"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                        style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                      >
                        {player.name?.charAt(0)}
                      </div>
                      <span className={`text-xs font-medium ${isLeader ? 'text-amber-300' : 'text-white'}`}>
                        {isWinner ? '👑 ' : isDealer ? '🃏 ' : ''}{player.name?.split(' ')[0]}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {ROUNDS.map((round, ri) => (
              <tr key={round} className={`border-t border-emerald-700/30 ${ri === currentRound && !gameComplete ? 'bg-emerald-700/20' : ''}`}>
                <td className={`p-1.5 font-bold text-xs text-center sticky left-0 z-10 ${ri === currentRound && !gameComplete ? 'bg-emerald-800 text-amber-200' : 'bg-emerald-900 text-amber-300'}`}>
                  {ri === currentRound && !gameComplete ? '▶ ' : ''}{round}
                </td>
                {players.map((player, pi) => {
                  const isDealer = currentDealer === pi && !gameComplete;
                  const scoreValue = scores[player.user_id]?.[ri] || '';
                  const isZero = scoreValue === '0';
                  const isWinner = gameComplete && getRank(player.user_id, leaderboard) === 0;
                  return (
                    <td key={player.user_id} className={`p-0.5 ${isDealer ? 'bg-amber-500/10' : ''} ${isWinner ? 'bg-amber-500/10' : ''}`}>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={scoreValue}
                        onChange={(e) => updateScore(player.user_id, ri, e.target.value)}
                        className={`w-full text-center rounded p-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400 ${
                          isZero
                            ? 'bg-green-500/30 text-green-300 font-bold'
                            : 'bg-emerald-800/50 text-white'
                        }`}
                        placeholder="–"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="bg-emerald-950 font-bold border-t-2 border-emerald-600">
              <td className="p-2 text-amber-400 text-xs sticky left-0 bg-emerald-950 z-10">TOTAL</td>
              {players.map((player, pi) => {
                const rank = getRank(player.user_id, leaderboard);
                const isDealer = currentDealer === pi && !gameComplete;
                const isLeader = rank === 0 && gameHasScores;
                const isWinner = gameComplete && rank === 0;
                return (
                  <td
                    key={player.user_id}
                    className={`p-2 text-center text-base ${isDealer ? 'bg-amber-500/10' : ''} ${isWinner ? 'bg-amber-500/20' : ''} ${isLeader ? 'text-amber-300' : 'text-white'}`}
                  >
                    {getTotal(player.user_id)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="text-center text-emerald-400 text-xs mt-2 space-y-0.5">
        <p>3-10 = face value • J=11 • Q=12 • K=13 • Joker=50</p>
        <p className="text-amber-300">Wild ({ROUNDS[currentRound] || 'K'}) = {currentRound <= 7 ? currentRound + 3 : currentRound === 8 ? 11 : currentRound === 9 ? 12 : 13} pts</p>
      </div>

      {/* Game Log */}
      <div className="mt-4 bg-emerald-950/80 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-emerald-700/30">
          <button
            onClick={() => setShowGameLog(!showGameLog)}
            className="flex items-center gap-2 text-amber-400 font-bold text-sm"
          >
            📋 Game Log
            <span className="text-emerald-400 text-xs">({gameLog.length})</span>
            <span className="text-emerald-500 text-xs">{showGameLog ? '▼' : '▶'}</span>
          </button>
          <button
            onClick={async () => {
              const success = await copyAllGameData();
              if (success) {
                setExportStatus('Copied!');
                setTimeout(() => setExportStatus(''), 2000);
              }
            }}
            className="bg-slate-600 hover:bg-slate-500 text-white px-3 py-1 rounded text-xs font-medium"
          >
            📋 Copy All Game Data
          </button>
        </div>
        {showGameLog && (
          <div className="max-h-40 overflow-y-auto">
            {gameLog.length === 0 ? (
              <div className="p-3 text-emerald-500 text-sm text-center">
                No activity yet. Score changes will appear here.
              </div>
            ) : (
              <div className="divide-y divide-emerald-800/50">
                {gameLog.map(log => (
                  <div key={log.id} className="px-3 py-2 flex items-start gap-2">
                    <span className="text-emerald-600 text-xs font-mono whitespace-nowrap">{log.time}</span>
                    <span className={`text-sm ${
                      log.type === 'edit' ? 'text-amber-300' :
                      log.type === 'reorder' ? 'text-purple-300' :
                      log.type === 'settings' ? 'text-cyan-300' :
                      'text-emerald-200'
                    }`}>
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Round Summary Popup */}
      {showRoundSummary && completedRound !== null && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-emerald-900 to-emerald-950 rounded-xl p-6 max-w-sm w-full border border-emerald-600/50 shadow-xl">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-amber-400">
                Round {ROUNDS[completedRound]} Complete!
              </h3>
              <p className="text-emerald-300 text-sm mt-1">
                {completedRound + 1 < ROUNDS.length
                  ? `Next round: ${ROUNDS[completedRound + 1]}`
                  : 'Final round complete!'}
              </p>
            </div>

            {/* Leaderboard */}
            <div className="bg-emerald-950/80 rounded-lg p-3 mb-4">
              <h4 className="text-amber-400 font-bold text-sm mb-2">🏆 Standings</h4>
              <div className="space-y-1">
                {getLeaderboard().map((player, i) => {
                  const rank = getRank(player.user_id, getLeaderboard());
                  return (
                    <div
                      key={player.user_id}
                      className={`flex items-center justify-between px-2 py-1.5 rounded ${
                        rank === 0 ? 'bg-amber-500/30' : 'bg-emerald-800/30'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{getMedal(rank)}</span>
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                        >
                          {player.name?.charAt(0)}
                        </div>
                        <span className={`text-sm font-medium ${rank === 0 ? 'text-amber-300' : 'text-white'}`}>
                          {player.name}
                        </span>
                      </div>
                      <span className={`font-bold font-mono ${rank === 0 ? 'text-amber-300' : 'text-white'}`}>
                        {player.total}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Next Dealer */}
            {completedRound + 1 < ROUNDS.length && (
              <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3 mb-4 text-center">
                <p className="text-amber-300 text-sm">🃏 Next Dealer</p>
                <p className="text-white font-bold text-lg">
                  {players[(dealerIndex + completedRound + 1) % players.length]?.name}
                </p>
              </div>
            )}

            <button
              onClick={() => setShowRoundSummary(false)}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg font-bold text-lg"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
